import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/context/AuthContext';
import { ActionBarProvider } from '@/contexts/ActionBarContext';
import { TenantProvider } from '@/contexts/TenantContext';
import { PermissionsProvider } from '@/contexts/PermissionsContext';
import { Toaster } from '@/components/ui/sonner';
import ProtectedRoute from '@/components/ProtectedRoute';
import CookieConsent from '@/components/CookieConsent';
import ErrorBoundary from '@/components/ErrorBoundary';

import Login from '@/pages/Login';
import ForcePasswordChange from '@/pages/ForcePasswordChange';
import PasswordReset from '@/pages/PasswordReset';
import StartScreen from '@/pages/Dashboard/StartScreen';
import StudentsList from '@/pages/Students/StudentsList';
import StudentDetail from '@/pages/Students/StudentDetail';
import StudentEdit from '@/pages/Students/StudentEdit';
import TeachersList from '@/pages/Teachers/TeachersList';
import TeacherDetail from '@/pages/Teachers/TeacherDetail';
import LocationsList from '@/pages/Locations/LocationsList';
import LocationDetail from '@/pages/Locations/LocationDetail';
import RoomFinder from '@/pages/Locations/RoomFinder';
import CoursesActiveList from '@/pages/Courses/CoursesActiveList';
import CoursesInactiveList from '@/pages/Courses/CoursesInactiveList';
import CourseDetail from '@/pages/Courses/CourseDetail';
import CourseEdit from '@/pages/Courses/CourseEdit';
import EnrollmentDetail from '@/pages/Enrollments/EnrollmentDetail';
import CancellationStatsPage from '@/pages/Reports/CancellationStatsPage';
import GkzAuswertungPage from '@/pages/Reports/GkzAuswertungPage';
import CalendarView from '@/pages/Calendar/CalendarView';
import FreeSlotsPage from '@/pages/Courses/FreeSlotsPage';
import LessonDetail from '@/pages/Lessons/LessonDetail';
import TariffsDiscounts from '@/pages/Billing/TariffsDiscounts';
import InvoicesList from '@/pages/Billing/InvoicesList';
import SepaPage from '@/pages/Billing/SepaPage';
import FixedAmountsPage from '@/pages/Billing/FixedAmountsPage';
import PrepaidCardsPage from '@/pages/Billing/PrepaidCardsPage';
import BillingPreviewPage from '@/pages/Billing/BillingPreviewPage';
import InstrumentRentalPage from '@/pages/Billing/InstrumentRentalPage';
import GuardiansPage from '@/pages/Guardians/GuardiansPage';
import PayerAccountsPage from '@/pages/Guardians/PayerAccountsPage';
import { Reports } from '@/pages/Reports';
import MsvImport from '@/pages/Import/MsvImport';
import FileMakerImportPage from '@/pages/Admin/FileMakerImportPage';
import Unterrichtsverwaltung from '@/pages/Teaching/Unterrichtsverwaltung';
import TrialLessonsPage from '@/pages/Teaching/TrialLessonsPage';
import FamiliesPage from '@/pages/Families/FamiliesPage';
import FamilyMoveHistoryPage from '@/pages/Families/FamilyMoveHistoryPage';
import Settings from '@/pages/Settings';
import MasterDataPage from '@/pages/MasterData/MasterDataPage';
import EmailPage from '@/pages/Email/EmailPage';
import CorrespondencePage from '@/pages/Correspondence/CorrespondencePage';
import PayrollPage from '@/pages/Payroll/PayrollPage';
import TenantsAdminPage from '@/pages/Tenants/TenantsAdminPage';
import RBACAdminPage from '@/pages/Admin/RBACAdminPage';
import AttendanceLists from '@/pages/Teaching/AttendanceLists';
import OnlineLessonRoom from '@/pages/Teaching/OnlineLessonRoom';
import AdminSelfServicePage from '@/pages/Admin/AdminSelfServicePage';
import MessagesPage from '@/pages/Messages/MessagesPage';
import MultiTenantOverview from '@/pages/Overview/MultiTenantOverview';
import ZentralePage from '@/pages/Admin/ZentralePage';

// Portal imports
import PortalLogin from '@/pages/Portal/PortalLogin';
import PortalDashboard from '@/pages/Portal/PortalDashboard';
import PortalChildren from '@/pages/Portal/PortalChildren';
import PortalInvoices from '@/pages/Portal/PortalInvoices';
import PortalContracts from '@/pages/Portal/PortalContracts';
import PortalSchedule from '@/pages/Portal/PortalSchedule';
import PortalSettings from '@/pages/Portal/PortalSettings';
import PortalMessagesPage from '@/pages/Portal/PortalMessagesPage';
import PortalTrialLessonPage from '@/pages/Portal/PortalTrialLessonPage';

// Public (Online-Anmeldung)
import PublicRegistrationWizard from '@/pages/Public/PublicRegistrationWizard';
import PublicRegistrationConfirm from '@/pages/Public/PublicRegistrationConfirm';
import PublicLegalDocument from '@/pages/Public/PublicLegalDocument';
import AccountDeletionPage from '@/pages/Public/AccountDeletionPage';
import PublicContractDeclaration from '@/pages/Public/PublicContractDeclaration';
import PublicConferenceJoin from '@/pages/Public/PublicConferenceJoin';
import SteuerberaterDrop from '@/pages/Public/SteuerberaterDrop';
import ConferencesPage from '@/pages/Conferences/ConferencesPage';
import ConferenceRoom from '@/pages/Conferences/ConferenceRoom';
import TeacherSlotMagicLinkPage from '@/pages/Public/TeacherSlotMagicLinkPage';
import ParentSlotMagicLinkPage from '@/pages/Public/ParentSlotMagicLinkPage';
import AdminRegistrationsPage from '@/pages/Admin/AdminRegistrationsPage';
import ContractDeclarationsPage from '@/pages/Admin/ContractDeclarationsPage';
import MailApprovalPage from '@/pages/Admin/MailApprovalPage';
import AppVerwaltungPage from '@/pages/AppVerwaltung';
import MobileApp from '@/mobile/MobileApp';
import { StagingBanner } from '@/components/layout/StagingBanner';
import TeacherAvailabilitySelfPage from '@/pages/Teacher/TeacherAvailabilitySelfPage';
import TeacherSlotRequestsPage from '@/pages/Teacher/TeacherSlotRequestsPage';
import TeacherPlusHoursPage from '@/pages/Teacher/TeacherPlusHoursPage';
import TeacherPayrollPage from '@/pages/Teacher/TeacherPayrollPage';

function App() {
  return (
    // Zwei Barrieren mit Absicht. Die äußere fängt alles, auch einen Fehler im
    // Toaster — genau der hatte die Oberfläche geleert. Die innere umschließt
    // nur die Seiten: stürzt eine ab, bleiben Anmeldung, Mandant und Router
    // stehen, und „Erneut versuchen“ genügt statt eines Neuladens.
    <ErrorBoundary>
    <AuthProvider>
      <TenantProvider>
        <PermissionsProvider>
          <BrowserRouter>
        <ActionBarProvider>
        <ErrorBoundary>
        <StagingBanner />
        <Routes>
          {/* Public Online-Anmeldung (Phase D1) — no auth */}
          <Route path="/app/konto-loeschen" element={<AccountDeletionPage />} />
          <Route path="/anmeldung/:tenantSlug" element={<PublicRegistrationWizard />} />
          <Route path="/anmeldung/:tenantSlug/kuendigung" element={<PublicContractDeclaration kind="cancellation" />} />
          <Route path="/anmeldung/:tenantSlug/widerruf" element={<PublicContractDeclaration kind="withdrawal" />} />
          <Route path="/anmeldung/:tenantSlug/rechtstext/:kind" element={<PublicLegalDocument />} />
          <Route path="/anmeldung/:tenantSlug/confirm/:token" element={<PublicRegistrationConfirm />} />
          <Route path="/konferenz/:guestToken" element={<PublicConferenceJoin />} />
          <Route path="/steuerberater/:token" element={<SteuerberaterDrop />} />
          <Route
            path="/conferences"
            element={
              <ProtectedRoute>
                <ConferencesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/conferences/:conferenceId/room"
            element={
              <ProtectedRoute>
                <ConferenceRoom />
              </ProtectedRoute>
            }
          />
          <Route path="/teacher-slot/:token" element={<TeacherSlotMagicLinkPage />} />
          <Route path="/parent-slot/:token" element={<ParentSlotMagicLinkPage />} />

          {/* Portal Routes (no admin auth required) */}
          <Route path="/portal/login" element={<PortalLogin />} />
          <Route path="/portal/dashboard" element={<PortalDashboard />} />
          <Route path="/portal/children" element={<PortalChildren />} />
          <Route path="/portal/invoices" element={<PortalInvoices />} />
          <Route path="/portal/contracts" element={<PortalContracts />} />
          <Route path="/portal/schedule" element={<PortalSchedule />} />
          <Route path="/portal/settings" element={<PortalSettings />} />
          <Route path="/portal/messages" element={<PortalMessagesPage />} />
          <Route path="/portal/trial-lesson" element={<PortalTrialLessonPage />} />
          <Route path="/portal" element={<Navigate to="/portal/login" replace />} />
          
          {/* Admin Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/passwort-aendern" element={<ForcePasswordChange />} />
          <Route path="/passwort-vergessen" element={<PasswordReset />} />
          <Route path="/passwort-zuruecksetzen" element={<PasswordReset />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <StartScreen />
              </ProtectedRoute>
            }
          />
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route
            path="/unterrichtsverwaltung"
            element={
              <ProtectedRoute resource="enrollments">
                <Unterrichtsverwaltung />
              </ProtectedRoute>
            }
          />
          <Route
            path="/families"
            element={
              <ProtectedRoute resource="families">
                <FamiliesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/students"
            element={
              <ProtectedRoute resource="students">
                <StudentsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/students/new"
            element={
              <ProtectedRoute resource="students">
                <StudentEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/students/:id/edit"
            element={
              <ProtectedRoute resource="students">
                <StudentEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/students/:id"
            element={
              <ProtectedRoute resource="students">
                <StudentDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teachers"
            element={
              <ProtectedRoute resource="teachers">
                <TeachersList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teachers/:id"
            element={
              <ProtectedRoute resource="teachers">
                <TeacherDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/locations"
            element={
              <ProtectedRoute resource="locations">
                <LocationsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/locations/:id"
            element={
              <ProtectedRoute resource="locations">
                <LocationDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/room-finder"
            element={
              <ProtectedRoute resource="locations">
                <RoomFinder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/courses"
            element={
              <ProtectedRoute resource="courses">
                <CoursesActiveList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/courses/inactive"
            element={
              <ProtectedRoute resource="courses">
                <CoursesInactiveList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/free-slots"
            element={
              <ProtectedRoute resource="courses">
                <FreeSlotsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/courses/:id/edit"
            element={
              <ProtectedRoute resource="courses">
                <CourseEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/courses/:id"
            element={
              <ProtectedRoute resource="courses">
                <CourseDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/enrollments/:id"
            element={
              <ProtectedRoute resource="enrollments">
                <EnrollmentDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/cancellations"
            element={
              <ProtectedRoute resource="cancellation_stats">
                <CancellationStatsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                <CalendarView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lessons/:id"
            element={
              <ProtectedRoute resource="lessons">
                <LessonDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tariffs-discounts"
            element={
              <ProtectedRoute anyResource={['tariffs', 'discounts']}>
                <TariffsDiscounts />
              </ProtectedRoute>
            }
          />
          {/* Legacy URL → Gebührenverwaltung, Tab Rabattgruppen */}
          <Route
            path="/discount-groups"
            element={<Navigate to="/tariffs-discounts" replace />}
          />
          {/* Legacy: teaching contracts UI removed — billing uses enrollments */}
          <Route path="/contracts" element={<Navigate to="/billing-preview" replace />} />
          <Route path="/contracts/*" element={<Navigate to="/billing-preview" replace />} />
          <Route
            path="/invoices"
            element={
              <ProtectedRoute resource="invoices">
                <InvoicesList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing-preview"
            element={
              <ProtectedRoute resource="billing">
                <BillingPreviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sepa"
            element={
              <ProtectedRoute resource="sepa">
                <SepaPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/guardians"
            element={
              <ProtectedRoute resource="payers">
                <GuardiansPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payer-accounts"
            element={
              <ProtectedRoute resource="payers">
                <PayerAccountsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/fixed-amounts"
            element={
              <ProtectedRoute resource="fixed_amounts">
                <FixedAmountsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/prepaid-cards"
            element={
              <ProtectedRoute resource="prepaid_cards">
                <PrepaidCardsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trial-lessons"
            element={
              <ProtectedRoute resource="lessons">
                <TrialLessonsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/gkz-auswertung"
            element={
              <ProtectedRoute resource="reports">
                <GkzAuswertungPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute resource="reports">
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute resource="settings">
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/stammdaten"
            element={
              <ProtectedRoute resource="stammdaten">
                <MasterDataPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/msv-import"
            element={
              <ProtectedRoute resource="imports">
                <MsvImport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/filemaker-import"
            element={
              <ProtectedRoute resource="imports">
                <FileMakerImportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/email"
            element={
              <ProtectedRoute>
                <EmailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/correspondence"
            element={
              <ProtectedRoute>
                <CorrespondencePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payroll"
            element={
              <ProtectedRoute>
                <PayrollPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tenants"
            element={
              <ProtectedRoute resource="tenants">
                <TenantsAdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/anwesenheitslisten"
            element={
              <ProtectedRoute resource="lessons">
                <AttendanceLists />
              </ProtectedRoute>
            }
          />
          <Route
            path="/online/:courseId"
            element={
              <ProtectedRoute resource="lessons">
                <OnlineLessonRoom />
              </ProtectedRoute>
            }
          />
          <Route
            path="/benutzerverwaltung"
            element={
              <ProtectedRoute resource="users">
                <RBACAdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instrument-rental"
            element={
              <ProtectedRoute resource="instruments">
                <InstrumentRentalPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/overview"
            element={
              <ProtectedRoute resource="overview">
                <MultiTenantOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/zentrale"
            element={
              <ProtectedRoute>
                <ZentralePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/family-moves"
            element={
              <ProtectedRoute resource="tenant_moves">
                <FamilyMoveHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/nachrichten"
            element={
              <ProtectedRoute resource="messages">
                <MessagesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/self-service-inbox"
            element={
              <ProtectedRoute resource="enrollments">
                <AdminSelfServicePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/registrations-inbox"
            element={
              <ProtectedRoute resource="enrollments">
                <AdminRegistrationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kuendigung-widerruf"
            element={
              <ProtectedRoute resource="enrollments">
                <ContractDeclarationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mail-approval"
            element={
              <ProtectedRoute>
                <MailApprovalPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app-verwaltung"
            element={
              <ProtectedRoute>
                <AppVerwaltungPage />
              </ProtectedRoute>
            }
          />
          <Route path="/m/*" element={<MobileApp />} />
          <Route
            path="/teacher/availability"
            element={
              <ProtectedRoute>
                <TeacherAvailabilitySelfPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/terminanfragen"
            element={
              <ProtectedRoute>
                <TeacherSlotRequestsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/plus-hours"
            element={
              <ProtectedRoute>
                <TeacherPlusHoursPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/payroll"
            element={
              <ProtectedRoute>
                <TeacherPayrollPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </ErrorBoundary>
        </ActionBarProvider>
        </BrowserRouter>
        </PermissionsProvider>
      </TenantProvider>
      <Toaster
        position="top-left"
        offset="10px"
        visibleToasts={3}
        style={{ left: 'max(1rem, 15.5rem)', top: '0.65rem' }}
        toastOptions={{
          classNames: {
            toast: 'group toast border border-slate-200 shadow-md max-w-[min(20rem,calc(100vw-18rem))]',
          },
        }}
      />
      <CookieConsent />
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
